import { useContext, useEffect, useId, useState } from 'react';
import { classnames } from 'helpers/utils';
import { Spinner } from 'common/spinner';
import { NotificationObject } from 'models/notificationObject';
import { appActions, AppContext } from 'context/app-context';
import { AlbumContentType, NotificationType } from 'common/enums';
import { Button, ButtonStyle } from 'common/button';
import { SelfRecordingContext } from 'context/self-recording-context';
import {
  VaultDetailContext,
  vaultDetailActions,
} from 'context/vault-detail-context';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import { publishMemoryAdded } from 'common/returning-onboarding/onboarding-status';
import styles from './rich-text-editor.module.scss';

interface RichTextEditorProps {
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  onClose: () => void;
  editId?: number;
  title?: string;
  onUpdateSuccess?: (updatedJournalDocument: JournalDocumentType) => void;
  // A new entry was saved (it may be after onClose: saving runs on).
  onCreated?: (journalDocument: JournalDocumentType) => void;
  // Leaves out the "Note added" message, for a host that says its own.
  quiet?: boolean;
  // The save button's label.
  saveLabel?: string;
}

export const RichTextEditor = ({
  value = '',
  placeholder = "What's on your mind?",
  disabled = false,
  onClose,
  editId,
  title,
  onUpdateSuccess,
  onCreated,
  quiet = false,
  saveLabel = 'Save note',
}: RichTextEditorProps) => {
  const [ReactQuill, setReactQuill] = useState<any>(null);
  const [bodyHtml, setBodyHtml] = useState(value ?? '');
  const { dispatch } = useContext(AppContext);
  const { dispatch: selfRecordingDispatch } = useContext(SelfRecordingContext);
  const { dispatch: vaultDetailDispatch } = useContext(VaultDetailContext);
  const {
    state: { albumId, vaultId, entryMeta },
  } = useContext(SelfRecordingContext);
  const uploadId = useId();
  const hasContent = bodyHtml?.replace(/<[^>]*>/g, '').trim().length > 0;

  const isEditMode = !!editId;

  const handleUpdate = async () => {
    if (!editId) return;
    try {
      const updatedJournalDocument = await VaultsController.updateJournalEntry(
        editId,
        {
          body: bodyHtml,
        },
      );
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Journal entry updated',
          type: NotificationType.Success,
        }),
      });
      onClose();
      onUpdateSuccess?.(updatedJournalDocument);
    } catch (error) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Failed to update journal entry',
          type: NotificationType.Error,
          message: 'Please try again later.',
        }),
      });
    }
  };

  const handleCreate = async () => {
    // Date/location picked in the Add Entry dialog; read before it closes.
    const meta = entryMeta;
    try {
      onClose();
      selfRecordingDispatch({
        type: selfRecordingActions.addUpload,
        addUpload: {
          type: AlbumContentType.write,
          // The placeholder card shows the real title — known client-side.
          name: title || 'Untitled',
          data: 'Rich text upload',
          isUploading: true,
          uploadId,
        },
      });

      const journalDocument = await VaultsController.addJournalEntry(
        albumId,
        { body: bodyHtml, name: title, ...meta },
        vaultId,
      );

      selfRecordingDispatch({
        type: selfRecordingActions.updateUpload,
        updateUpload: {
          type: AlbumContentType.write,
          data: 'Rich text upload',
          isUploading: false,
          error: false,
          uploadId,
        },
      });
      const response = {
        ...journalDocument,
        contentType: AlbumContentType.write,
      };
      selfRecordingDispatch({
        type: selfRecordingActions.setUploadResponse,
        uploadResponse: {
          uploadId,
          response,
        },
      });
      // The album screen patches its own list; everywhere else (the top-bar
      // "New" modal, including notes filed under no album) needs the vault
      // re-read so the new memory shows without a reload.
      vaultDetailDispatch({ type: vaultDetailActions.REQUEST_ALBUMS_REFRESH });
      publishMemoryAdded();
      onCreated?.(journalDocument);
      if (!quiet) {
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'Note added',
            type: NotificationType.Success,
          }),
        });
      }
    } catch (error) {
      selfRecordingDispatch({
        type: selfRecordingActions.updateUpload,
        updateUpload: {
          isUploading: false,
          error: true,
          uploadId: 'journal-entry',
        },
      });
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Failed to save journal entry',
          type: NotificationType.Error,
          message: 'Please try again later.',
        }),
      });
    }
  };

  const handleSave = isEditMode ? handleUpdate : handleCreate;

  useEffect(() => {
    const link = document.createElement('link');
    link.href = '//cdn.quilljs.com/1.2.6/quill.snow.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    const loadQuill = async () => {
      try {
        const module = await import('react-quill-new');
        setReactQuill(() => module.default);
      } catch (error) {
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'Something went wrong',
            message:
              'Failed to load the Rich text editor. Please try again later.',
            type: NotificationType.Error,
          }),
        });
      }
    };

    loadQuill();

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const handleEditorChange = (content: string) => {
    setBodyHtml(content);
  };

  if (!ReactQuill) {
    return <Spinner />;
  }

  const modules = {
    toolbar: [
      ['bold', 'italic', 'blockquote'],
      ['link'],
      [{ list: 'ordered' }, { list: 'bullet' }],
    ],
  };

  const formats = ['bold', 'italic', 'blockquote', 'link', 'list'];

  return (
    <div className={styles.addNoteContainer}>
      <ReactQuill
        value={bodyHtml}
        onChange={handleEditorChange}
        placeholder={placeholder}
        readOnly={disabled}
        modules={modules}
        formats={formats}
        className={classnames(styles.quill, {
          [styles.disabled]: disabled,
        })}
        theme="snow"
      />
      <div className={styles.addNoteButtons}>
        <Button buttonStyle={ButtonStyle.SecondaryGray} onClick={onClose}>
          Discard
        </Button>
        <Button
          buttonStyle={ButtonStyle.Primary}
          onClick={handleSave}
          disabled={!hasContent}
        >
          {saveLabel}
        </Button>
      </div>
    </div>
  );
};
